import {FormArray, FormGroup} from '@angular/forms';

import * as moment from 'moment';

import {IndividualIdentityDetailModel} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator, MinDateValidator} from '@spa/validators';

import {Helper, BaseControl, DatepickerControl, SelectControl} from '../../../../shared/classes';

import {
    IdentityDetailControls,
    IdentityDetailIdSelector,
    IdentityDocumentTypeSelector,
    IdentityDocumentNumberSelector,
    IdentityDocumentStatusSelector,
    IdentityDocumentExpirationDateSelector,
} from '../../../../../assets/descriptors/panel-clients';

export class IdentityDetailsTrait {
    private constructor() {
    }

    static initControls(
        skipStatusControl: boolean,
        model?: IndividualIdentityDetailModel,
        mappedControls: BaseControl[] = []
    ): BaseControl[] {
        const identityDocumentTypes = Helper.enumsHelper().getProofOfIdentityDocumentTypes();

        let controls = mappedControls.length !== 0 ? mappedControls : IdentityDetailControls();

        if (skipStatusControl) {
            controls = controls.filter((control: BaseControl) => control.name !== IdentityDocumentStatusSelector);
        }

        return controls.map((control: BaseControl) => {
            if (model) {
                control.value = IdentityDetailsTrait.getIdentityDetailProperty(control.name, model);
            }

            switch (control.name) {
                case IdentityDocumentTypeSelector:
                    (control as SelectControl).options = identityDocumentTypes.getTypeKeys().map((id: string) => {
                        return {
                            label: identityDocumentTypes.getTypeValue(id),
                            value: id
                        };
                    });

                    control.extendValidators(InArrayValidator(identityDocumentTypes.getTypeKeys()));
                    break;
                case IdentityDocumentExpirationDateSelector:
                    if (model && !model.expirationDate) {
                        control.disabled = true;
                    }

                    const minDate =
                        !control.value ? moment().format('YYYY-MM-DD') : moment(control.value).format('YYYY-MM-DD');

                    (control as DatepickerControl).minDate = minDate;

                    control.extendValidators(MinDateValidator(minDate));
                    break;
                case IdentityDocumentStatusSelector:
                    (control as SelectControl).options = Helper.enumsHelper().getIdentityDocumentStatuses()
                        .map((status: string) => {
                            return {
                                label: Helper.textsHelper().getIdentityDocumentStatusName(status),
                                value: status
                            };
                        });

                    control.extendValidators(InArrayValidator(Helper.enumsHelper().getIdentityDocumentStatuses()));
                    break;
            }

            return control;
        });
    }

    static getIdentityDetailProperty(selector: string, model: IndividualIdentityDetailModel): string | number {
        switch (selector) {
            case IdentityDetailIdSelector:
                return model.id;
            case IdentityDocumentTypeSelector:
                return model.identityDocumentType;
            case IdentityDocumentNumberSelector:
                return model.identityNumber;
            case IdentityDocumentExpirationDateSelector:
                return model.expirationDate;
            case IdentityDocumentStatusSelector:
                return model.reviewStatus;
            default:
                return null;
        }
    }

    static checkForDuplicatedNumbers(
        controls: Array<BaseControl[]>,
        formArray: FormArray,
        formGroup: FormGroup,
        formGroupIndex: number,
        messageFunction: (controlLabel?: string) => { [key: string]: string; }
    ): boolean {
        const duplicatedControls = [];

        const statuses = formArray.controls.map((group: FormGroup, groupIndex: number) => {
            if (
                (formGroup.value[IdentityDocumentTypeSelector] === group.value[IdentityDocumentTypeSelector]) &&
                (formGroup.value[IdentityDocumentNumberSelector] === group.value[IdentityDocumentNumberSelector]) &&
                (formGroupIndex !== groupIndex)
            ) {
                controls[formGroupIndex]
                    .filter((control: BaseControl) => control.name === IdentityDocumentNumberSelector)
                    .map((control: BaseControl) => duplicatedControls.push(control));

                return false;
            }

            return true;
        });

        /**
         * Show message below all controls except the first.
         */
        duplicatedControls.filter((control: BaseControl, index: number) => index > 0).map((control: BaseControl) => {
            const errorMessage = messageFunction(control.placeholder)[DuplicatedValueValidatorKey];

            control.setError(errorMessage);
        });


        return statuses.indexOf(false) < 0;
    }

    static removeEmptyGroups(controls: Array<BaseControl[]>, emptyGroupIndexes: number[], formArray: FormArray): void {
        emptyGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);

            controls.splice(index, 1);
        });
    }


    static removeUnchangedIdentityDetails(
        identityDetails: IndividualIdentityDetailModel[],
        formGroup: FormGroup,
    ): boolean {
        const filteredIdentityDetails = identityDetails.filter((detail: IndividualIdentityDetailModel) => {
            return detail.id === formGroup.controls[IdentityDetailIdSelector].value;
        });

        if (filteredIdentityDetails.length === 0) {
            return false;
        }

        const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
            const formControlValue = formGroup.controls[formControlKey].value;

            switch (formControlKey) {
                case IdentityDetailIdSelector:
                    return filteredIdentityDetails[0].id === formControlValue;
                case IdentityDocumentTypeSelector:
                    return filteredIdentityDetails[0].identityDocumentType === formControlValue;
                case IdentityDocumentNumberSelector:
                    return filteredIdentityDetails[0].identityNumber === formControlValue;
                case IdentityDocumentExpirationDateSelector:
                    return filteredIdentityDetails[0].expirationDate === formControlValue;
                case IdentityDocumentStatusSelector:
                    return filteredIdentityDetails[0].reviewStatus === formControlValue;
                default:
                    return false;
            }
        });

        return valuesCheck.indexOf(false) >= 0;
    }
}
